import { z } from 'zod';
import { ACCOUNT_TYPES, CASH_FLOW_CATEGORIES } from '../constants/accountTypes.js';

export const accountTypeSchema = z.enum(ACCOUNT_TYPES);
export const normalBalanceSchema = z.enum(['debit', 'credit']);
export const cashFlowCategorySchema = z.enum(CASH_FLOW_CATEGORIES);

export const createAccountInputSchema = z.object({
  // Optional — when blank, the server auto-assigns a code in the account type's range.
  code: z.string().max(20).regex(/^[A-Za-z0-9._-]*$/, 'use letters, digits, . _ -').optional(),
  name: z.string().min(1).max(200),
  accountType: accountTypeSchema,
  accountSubtype: z.string().max(50).nullable().optional(),
  parentAccountId: z.string().uuid().nullable().optional(),
  description: z.string().max(1000).optional(),
  isSystem: z.boolean().default(false),
  allowDirectPosting: z.boolean().default(true),
  normalBalance: normalBalanceSchema.optional(),
  cashFlowCategory: cashFlowCategorySchema.optional(),
  taxCode: z.string().max(20).optional(),
  /**
   * Does this account get tied to an outside statement? Only flagged accounts
   * are offered on the reconcile screen — every asset and liability being
   * technically reconcilable made that list long enough to mis-pick from.
   */
  isReconcilable: z.boolean().optional(),
  /**
   * May checks be written against this account? Only flagged accounts appear in
   * the check writer's "pay from" list, for the same reason isReconcilable
   * exists — the full chart is too long to pick from safely.
   */
  allowCheckWriting: z.boolean().optional(),
});
export type CreateAccountInput = z.infer<typeof createAccountInputSchema>;

export const updateAccountInputSchema = createAccountInputSchema
  .partial()
  .omit({ isSystem: true })
  .extend({
    isActive: z.boolean().optional(),
    /**
     * Three states on an update: undefined leaves the parent alone, null clears
     * it (the account becomes top level), a uuid re-parents it.
     */
    parentAccountId: z.string().uuid().nullable().optional(),
    /**
     * No default on the update side. `.partial()` keeps the create schema's
     * `.default(true)`, and the route's JSON schema validator applies defaults,
     * so a PATCH that did not mention this field would silently turn a header
     * account back into a postable one.
     */
    allowDirectPosting: z.boolean().optional(),
  });
export type UpdateAccountInput = z.infer<typeof updateAccountInputSchema>;

export const accountSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  accountType: accountTypeSchema,
  accountSubtype: z.string().nullable(),
  parentAccountId: z.string().uuid().nullable(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  allowDirectPosting: z.boolean(),
  normalBalance: normalBalanceSchema,
  cashFlowCategory: cashFlowCategorySchema.nullable(),
  taxCode: z.string().nullable(),
  isIntercompany: z.boolean().default(false),
  isReconcilable: z.boolean().default(false),
  allowCheckWriting: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Account = z.infer<typeof accountSchema>;

export const accountWithBalanceSchema = accountSchema.extend({
  balance: z.string(),
});
export type AccountWithBalance = z.infer<typeof accountWithBalanceSchema>;
